import { Column } from '../../../grids';
import { DrawApi } from '@apply/api';
import { formatCurrencyOrUndefined } from '../../../../../../ui/src';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { calculateBalance } from '../functions';
import React from 'react';

interface IDrawAutomaticColumnProps {
    drawId: number;
    drawNumber: number;
    drawFinalized: boolean;
    toggleArray: boolean[];
    applicationInsights: ApplicationInsights;
    set_drawBudget: Dispatch<SetStateAction<DrawApi.IDrawBudget | undefined>>;
    set_isLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const DrawAutomaticColumn = ({
    drawId,
    drawNumber,
    drawFinalized,
    toggleArray,
    applicationInsights,
    set_drawBudget,
    set_isLoading,
}: IDrawAutomaticColumnProps): ReactElement[] => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    function copyInspectionToFunded(drawApi: DrawApi.DrawApi, drawId: number) {
        set_isLoading(true);
        drawApi
            .copyInspectionToFunded(drawId)
            .then((data) => set_drawBudget(data.result))
            .finally(() => set_isLoading(false));
    }

    const calculateAccommodation = (data: DrawApi.IDrawBudgetItem) => {
        return calculateBalance(drawNumber, data.drawItems) ?? '';
    };

    const inspectionColumnHeader = () => {
        return (
            <div style={{ width: 90, textAlign: 'center' }}>
                <p>INSPECTION</p>
                <button
                    type='button'
                    onClick={() => copyInspectionToFunded(drawApi, drawId)}
                    className='button hollow'
                    title='Copy to Funded'
                >
                    <FontAwesomeIcon icon={faCopy} />
                </button>
            </div>
        );
    };

    const precision = drawFinalized ? 0 : 2;
    if (toggleArray[drawNumber] === true) {
        return [
            <Column
                name={`AutomaticInspection${drawId}-${drawNumber}`}
                key={`AutomaticInspection${drawId}-${drawNumber}`}
                dataField={`drawItems-inspection-${drawId}-${drawNumber}`}
                dataType='number'
                editorOptions={{
                    dataType: 'number',
                    format: { type: 'currency', precision: precision },
                    step: 0,
                }}
                headerCellRender={inspectionColumnHeader}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return formatCurrencyOrUndefined(dataItem?.inspection, 2);
                }}
                format={'currency'}
                allowResizing={false}
                alignment={'right'}
                minWidth={100}
                allowEditing={!drawFinalized}
                width={100}
            />,
            <Column
                name={`AutomaticFunded${drawId}-${drawNumber}`}
                key={`AutomaticFunded${drawId}-${drawNumber}`}
                dataType='number'
                editorOptions={{
                    dataType: 'number',
                    format: { type: 'currency', precision: precision },
                    step: 0,
                }}
                dataField={`drawItems-funded-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return formatCurrencyOrUndefined(dataItem?.funded, 2);
                }}
                caption='FUNDED'
                format={'currency'}
                allowResizing={false}
                minWidth={100}
                alignment={'right'}
                allowEditing={!drawFinalized}
                width={100}
            />,
            <Column
                name={`Accommodation${drawId}-${drawNumber}`}
                key={`Accommodation${drawId}-${drawNumber}`}
                calculateCellValue={calculateAccommodation}
                cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, precision)}
                caption='ACCOMMODATION'
                format={'currency'}
                allowResizing={false}
                minWidth={125}
                alignment={'right'}
                allowEditing={false}
                width={125}
            />,
            <Column
                name={`AutomaticNotes${drawId}-${drawNumber}`}
                key={`AutomaticNotes${drawId}-${drawNumber}`}
                dataField={`drawItems-notes-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return dataItem?.notes;
                }}
                caption='NOTES'
                format={'currency'}
                allowResizing={false}
                minWidth={275}
                allowEditing={!drawFinalized}
                width={275}
            />,
        ];
    } else {
        if (drawFinalized) {
            return [
                <Column
                    name={`Column${drawId}-${drawNumber}`}
                    key={`Column${drawId}-${drawNumber}`}
                    dataField={`drawItems-funded-${drawId}-${drawNumber}`}
                    cellRender={(cellData) => {
                        const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                        return formatCurrencyOrUndefined(dataItem?.funded, 2);
                    }}
                    allowEditing={false}
                    caption={`DRAW #${drawNumber + 1}`}
                    format={'currency'}
                    alignment={'right'}
                    allowResizing={false}
                    minWidth={100}
                    width={100}
                />,
            ];
        } else {
            return [
                <Column
                    key={`Column${drawId}-${drawNumber}`}
                    dataField={`drawItems-inspection-${drawId}-${drawNumber}`}
                    cellRender={(cellData) => {
                        const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                        return formatCurrencyOrUndefined(dataItem?.inspection, 2);
                    }}
                    allowEditing={false}
                    caption={`DRAW #${drawNumber + 1}`}
                    format={'currency'}
                    alignment={'right'}
                    allowResizing={false}
                    minWidth={100}
                    width={100}
                />,
            ];
        }
    }
};
